import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

/**
 * SignatureUploadGuide Component
 * 
 * Provides visual guidance and instructions for uploading a proper digital signature.
 * Shows examples of good vs bad signatures and technical requirements.
 * 
 * @component
 */
const SignatureUploadGuide = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
      {/* Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-blue-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="Info" className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h4 className="font-semibold text-blue-900">How to Upload a Good Digital Signature</h4>
            <p className="text-sm text-blue-700">Click to view guidelines and examples</p>
          </div>
        </div>
        <Icon 
          name={isExpanded ? "ChevronUp" : "ChevronDown"} 
          className="w-5 h-5 text-blue-700 flex-shrink-0" 
        />
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-blue-200">
          {/* Requirements Section */}
          <div className="mt-4 bg-white rounded-lg p-4 border border-blue-100">
            <h5 className="font-semibold text-blue-900 mb-3 flex items-center">
              <Icon name="CheckCircle2" className="w-5 h-5 mr-2 text-green-600" />
              Technical Requirements
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
              <div className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span><strong>Format:</strong> JPG, PNG (PNG with transparency preferred)</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span><strong>Size:</strong> Maximum 5MB</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span><strong>Dimensions:</strong> Recommended 400×200 pixels (2:1 ratio)</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span><strong>Background:</strong> White or transparent preferred</span>
              </div>
            </div>
          </div>

          {/* Good vs Bad Examples */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Good Examples */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <Icon name="Check" className="w-4 h-4 text-white" />
                </div>
                <h5 className="font-semibold text-green-900">✅ DO - Good Examples</h5>
              </div>
              <ul className="space-y-2 text-sm text-green-800">
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>Clean signature on transparent or white background</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>High resolution, clear lines</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>Signature created digitally (using drawing tablet or signature pad)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>PNG format with transparency (preferred)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>Properly cropped with minimal empty space</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>Black or dark blue ink color</span>
                </li>
              </ul>
            </div>

            {/* Bad Examples */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                  <Icon name="X" className="w-4 h-4 text-white" />
                </div>
                <h5 className="font-semibold text-red-900">❌ DON'T - Bad Examples</h5>
              </div>
              <ul className="space-y-2 text-sm text-red-800">
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>Photo of signature on paper (with shadows, creases)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>Low resolution, pixelated, or blurry images</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>Colored or patterned backgrounds</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>Including typed text, stamps, or dates</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>Multiple signatures in one image</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="mt-1">•</span>
                  <span>Extremely large file sizes (over 5MB)</span>
                </li>
              </ul>
            </div>
          </div>

          {/* How to Create Section */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <h5 className="font-semibold text-blue-900 mb-3 flex items-center">
              <Icon name="Lightbulb" className="w-5 h-5 mr-2 text-yellow-500" />
              How to Create a Digital Signature
            </h5>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <p className="font-medium text-gray-900 mb-1">Option 1: Use a Signature Pad/Tablet</p>
                <p>Sign using a digital signature pad or drawing tablet, then save as PNG with transparency.</p>
              </div>
              <div>
                <p className="font-medium text-gray-900 mb-1">Option 2: Scan Your Signature</p>
                <ol className="list-decimal ml-5 space-y-1 mt-2">
                  <li>Sign on white paper with black/blue pen</li>
                  <li>Scan at 300 DPI or higher</li>
                  <li>Use image editing software to remove background</li>
                  <li>Save as PNG with transparency</li>
                </ol>
              </div>
              <div>
                <p className="font-medium text-gray-900 mb-1">Option 3: Use Online Tools</p>
                <p>Use free online signature creators like:</p>
                <ul className="list-disc ml-5 space-y-1 mt-2">
                  <li>Docsketch, SignWell, or similar tools</li>
                  <li>Draw your signature using mouse or touchscreen</li>
                  <li>Download as PNG</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Important Note:</p>
                <p>
                  This digital signature will appear on all your vendor agreements including NDA, 
                  SA, 4M Change Control, Code of Conduct, Compliance Agreement, Overseas Agreement, and Self Declaration. 
                  Ensure it is your authorized signatory's actual signature.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureUploadGuide;



